import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, Receipt, AlertCircle, Wallet, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { gbp, INVOICE_STATUSES, INVOICE_STATUS_COLORS, statusLabel } from "@/lib/invoice";

export default function Invoices() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["invoices", status, search],
    queryFn: async () => (await api.get("/admin/invoices", { params: { status, search } })).data,
  });
  const { data: summary } = useQuery({
    queryKey: ["invoices-summary"],
    queryFn: async () => (await api.get("/admin/invoices/summary")).data,
  });

  return (
    <div data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-black text-slate-900 text-2xl sm:text-3xl">Invoices</h1>
          <p className="text-slate-500 mt-1">Create, track and get paid.</p>
        </div>
        <button onClick={() => navigate("/admin/invoices/new")} data-testid="invoice-new-btn" className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-green-700">
          <Plus className="h-4.5 w-4.5" /> <span className="hidden sm:inline">New invoice</span>
        </button>
      </div>

      {summary && (
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm" data-testid="summary-outstanding">
            <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-600 grid place-items-center"><Wallet className="h-5 w-5" /></div>
            <div className="mt-3 text-2xl font-display font-black text-slate-900">{gbp(summary.outstanding)}</div>
            <div className="text-xs text-slate-500 font-medium">Outstanding</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm" data-testid="summary-paid">
            <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center"><Receipt className="h-5 w-5" /></div>
            <div className="mt-3 text-2xl font-display font-black text-slate-900">{gbp(summary.paid_total)}</div>
            <div className="text-xs text-slate-500 font-medium">Collected</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm" data-testid="summary-overdue">
            <div className="h-10 w-10 rounded-xl bg-red-50 text-red-600 grid place-items-center"><AlertCircle className="h-5 w-5" /></div>
            <div className="mt-3 text-2xl font-display font-black text-slate-900">{summary.overdue}</div>
            <div className="text-xs text-slate-500 font-medium">Overdue</div>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search number, customer, phone…" data-testid="invoice-search"
            className="w-full rounded-xl border border-slate-300 bg-white pl-11 pr-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100" />
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", ...INVOICE_STATUSES].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`invoice-filter-${s}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${status === s ? "bg-green-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {s === "all" ? "All" : statusLabel(s)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <p className="text-slate-500">Loading…</p>
        ) : invoices.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">No invoices yet. Create one, or convert a lead from the Leads page.</div>
        ) : (
          <ul className="space-y-3">
            {invoices.map((inv) => (
              <li key={inv.id}>
                <Link to={`/admin/invoices/${inv.id}`} data-testid={`invoice-row-${inv.id}`} className="block rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-sm hover:shadow-md hover:border-green-200 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-slate-900 text-sm">{inv.invoice_number}</span>
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${INVOICE_STATUS_COLORS[inv.status]}`}>{statusLabel(inv.status)}</span>
                      </div>
                      <div className="font-semibold text-slate-800 mt-1">{inv.customer_name || "—"}</div>
                      <div className="text-xs text-slate-400 mt-1">Due {inv.due_date || "—"}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display font-black text-slate-900 text-lg">{gbp(inv.total)}</div>
                      {inv.balance_due > 0 && inv.amount_paid > 0 && <div className="text-xs text-amber-600 font-semibold">{gbp(inv.balance_due)} due</div>}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
